import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { FileIcon, Upload, X } from 'lucide-react';
import { useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
	getTilesetTypeForFileName,
	SPLAT_SH_OPTIONS,
	SPLAT_TYPE,
	TILESET_ACCEPTED_EXTENSIONS,
	type TilesetShOption,
	type TilesetType,
} from '@/common/data-upload';
import { Button } from '@/components/ui/button';
import { DialogClose, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTilesetUpload } from '@/context/TilesetUploadContext';
import { useUploadBusy } from '@/hooks/use-upload-busy';
import { sampleGroundHeight } from '@/lib/utils/ground-height';
import { getAssetOptions } from '@/queries/assets/get-asset';

import { Label } from '../../ui/label';
import { type Add3DDataPanelProps, AssetStructureField, NameField } from './Add3DDataFields';

const shLabels = (t: TFunction): Record<TilesetShOption, string> => ({
	full: t('files.tileset.shFull'),
	none: t('files.tileset.shNone'),
});

const coordinateToInput = (value: number | null | undefined) => (value == null ? '' : String(value));

// Assets saved before the location became mandatory store 0 for "no location" (the 3D viewer's
// InitialZoomController reads it the same way), so a 0 must not place a model at 0°, 0°.
const isAssetLocation = (lon: number | null | undefined, lat: number | null | undefined) =>
	lon != null && lat != null && lon !== 0 && lat !== 0;

// Sources that can carry their own georeference, which the converter applies only
// when the job sends no position (an explicit position wins). Point clouds join
// this list once they are offered (LAS CRS).
const SELF_GEOREFERENCED_TYPES: readonly TilesetType[] = ['bim'];

/**
 * Reads the optional initial position from the two inputs. Both empty means
 * "position later via Set Location"; anything else must be a valid pair.
 */
const parseInitialPosition = (
	lonInput: string,
	latInput: string,
	t: TFunction,
): { lon?: number; lat?: number } | { error: string } => {
	if (lonInput.trim() === '' && latInput.trim() === '') return {};
	const lon = Number(lonInput);
	const lat = Number(latInput);
	if (lonInput.trim() === '' || latInput.trim() === '' || Number.isNaN(lon) || Number.isNaN(lat)) {
		return { error: t('files.validation.positionBothRequired') };
	}
	if (lon < -180 || lon > 180 || lat < -90 || lat > 90) {
		return { error: t('files.validation.positionRange') };
	}
	return { lon, lat };
};

/**
 * "Upload file" panel of Add3DDataDialog: hands a .glb/.ply/.splat/.dxf/.ifc source to
 * the tile server for conversion. The model is positioned afterwards via "Set
 * Location" in the list.
 */
export function UploadGlbData({
	onAdd,
	onClose,
	assetId,
	inspectionId,
	damageId,
	name,
	setName,
	assetStructure,
}: Add3DDataPanelProps) {
	const { t } = useTranslation();
	const { startUpload } = useTilesetUpload();
	// A transfer already running (model or plain file) blocks starting another one.
	// Only this panel is gated: the Cesium ION tab records an asset ID and moves no
	// bytes, so an upload in flight is no reason to keep it shut.
	const isUploadBusy = useUploadBusy();
	// Already fetched by the dialog's asset-structure hook, so this reads the cache.
	const { data: asset } = useQuery(getAssetOptions(assetId || ''));
	const [file, setFile] = useState<File | null>(null);
	const [sh, setSh] = useState<TilesetShOption>('full');
	// Initial position as typed by the user; null until touched, so the default
	// below keeps applying (an asset that loads late still fills the fields).
	const [typedPosition, setTypedPosition] = useState<{ lon: string; lat: string } | null>(null);
	// True while the ground height for the initial position is being looked up
	const [isPlacing, setIsPlacing] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const positionFieldId = useId();
	const shLabelsMap = useMemo(() => shLabels(t), [t]);

	// Resolve the tile server type from the picked file's extension
	// (see getTilesetTypeForFileName for the extension -> type mapping).
	const tilesetType = file ? getTilesetTypeForFileName(file.name) : null;
	const isSplat = tilesetType === SPLAT_TYPE;
	// Default the position to the asset location so a model lands next to its asset
	// after conversion — except for a source that may carry its own georeference,
	// which starts empty so the converter can read it.
	const isSelfGeoreferenced = tilesetType != null && SELF_GEOREFERENCED_TYPES.includes(tilesetType);
	const assetPosition = isAssetLocation(asset?.longitude, asset?.latitude)
		? { lon: coordinateToInput(asset?.longitude), lat: coordinateToInput(asset?.latitude) }
		: null;
	const defaultPosition = isSelfGeoreferenced || !assetPosition ? { lon: '', lat: '' } : assetPosition;
	const position = typedPosition ?? defaultPosition;
	// Validated on every change so the form shows the problem instead of a toast on submit
	const parsedPosition = parseInitialPosition(position.lon, position.lat, t);
	const positionError = 'error' in parsedPosition ? parsedPosition.error : null;
	const acceptAttr = TILESET_ACCEPTED_EXTENSIONS.join(',');
	const acceptedLabel = `${TILESET_ACCEPTED_EXTENSIONS.slice(0, -1).join(', ')} or ${TILESET_ACCEPTED_EXTENSIONS.at(-1)}`;

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const selected = e.target.files?.[0];
		if (selected) {
			if (!getTilesetTypeForFileName(selected.name)) {
				toast.error(t('files.upload.selectFileType', { formats: acceptedLabel }));
				return;
			}
			setFile(selected);
			// Default the editable name to the file name (without extension)
			setName(selected.name.replace(/\.[^.]+$/, ''));
		}
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!file || !tilesetType) {
			toast.error(t('files.upload.selectFileType', { formats: acceptedLabel }));
			return;
		}
		if (!assetId) {
			toast.error(t('files.upload.assetIdRequired'));
			return;
		}
		if ('error' in parsedPosition) return; // the form already shows the message and blocks the button
		const { lon, lat } = parsedPosition;

		// The converter places a positioned model right away, so it needs a real height: the ground
		// at that point. Without one the model waits for "Set Location" instead of an arbitrary height.
		let groundHeight: number | null = null;
		if (lon != null && lat != null) {
			setIsPlacing(true);
			groundHeight = await sampleGroundHeight(lon, lat);
			setIsPlacing(false);
			if (groundHeight == null) toast.info(t('files.upload.groundHeightUnknown'));
		}
		const placement = groundHeight != null ? { lon, lat, height: groundHeight } : {};

		// Hand off to the background upload manager and close immediately.
		startUpload({
			file,
			type: tilesetType,
			sh: isSplat ? sh : undefined,
			name: name.trim() || file.name,
			...placement,
			anchor: 'origin',
			assetId,
			subAssetId:
				assetStructure.selectedId && assetStructure.selectedId !== assetId ? assetStructure.selectedId : undefined,
			inspectionId: inspectionId || undefined,
			damageId: damageId || undefined,
		});

		onAdd?.();
		onClose();
	};

	return (
		<form onSubmit={onSubmit}>
			<div className="space-y-4 py-4">
				{/* File picker */}
				<div>
					<Label className="text-sm font-medium mb-2 block">
						{t('files.upload.modelFileLabel', { formats: acceptedLabel })} <span className="text-red-500">*</span>
					</Label>
					<input type="file" accept={acceptAttr} className="hidden" ref={fileInputRef} onChange={handleFileChange} />
					{!file ? (
						<Button
							type="button"
							variant="outline"
							className="w-full justify-start bg-input p-3 py-5"
							onClick={() => fileInputRef.current?.click()}
						>
							<Upload className="size-4 mr-2" />
							{t('files.upload.selectFileButton', { formats: acceptedLabel })}
						</Button>
					) : (
						<div className="flex items-center justify-between p-2 border rounded bg-card">
							<div className="flex items-center gap-2 overflow-hidden">
								<FileIcon className="size-4 shrink-0" />
								<div className="flex flex-col overflow-hidden">
									<span className="text-sm truncate font-medium">{file.name}</span>
									<span className="text-[10px] text-muted-foreground">{(file.size / (1024 * 1024)).toFixed(2)} MB</span>
								</div>
							</div>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								className="size-6 text-muted-foreground hover:text-red-500"
								onClick={() => setFile(null)}
							>
								<X className="size-4" />
							</Button>
						</div>
					)}
				</div>

				{/* Editable display name (defaults to the file name) */}
				{file && <NameField value={name} onChange={setName} />}

				{/* Detected tile server type (informational) */}
				{file && tilesetType && (
					<p className="text-[11px] text-muted-foreground">{t('files.upload.detectedType', { type: tilesetType })}</p>
				)}

				{tilesetType === 'bim' && <p className="text-[11px] text-muted-foreground">{t('files.upload.ifcHint')}</p>}

				{/* Splat Options (gaussian splat / .ply/.splat): spherical harmonics */}
				{isSplat && (
					<div>
						<Label className="mb-1 block text-sm font-medium">{t('files.upload.splatOptionsLabel')}</Label>
						<Select value={sh} onValueChange={(v) => setSh(v as TilesetShOption)}>
							<SelectTrigger className="bg-white w-full">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{SPLAT_SH_OPTIONS.map((opt) => (
									<SelectItem key={opt} value={opt}>
										{shLabelsMap[opt]}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<p className="mt-1 text-[11px] text-muted-foreground">{t('files.upload.splatHint')}</p>
					</div>
				)}

				<AssetStructureField state={assetStructure} />

				{/* Initial position, prefilled from the asset. Empty = position later via Set Location. */}
				<div>
					<div className="flex items-center justify-between mb-2">
						<Label className="text-sm font-medium">{t('files.upload.initialPositionLabel')}</Label>
						{isSelfGeoreferenced && assetPosition && (
							<Button
								type="button"
								variant="ghost"
								size="xs"
								className="cursor-pointer"
								onClick={() => setTypedPosition(assetPosition)}
								title={t('files.upload.useAssetLocationHint')}
							>
								{t('files.upload.useAssetLocation')}
							</Button>
						)}
					</div>
					<div className="grid grid-cols-2 gap-2">
						<div>
							<Label htmlFor={`${positionFieldId}-lon`} className="text-xs text-muted-foreground mb-1 block">
								{t('common.labels.longitude')}
							</Label>
							<Input
								id={`${positionFieldId}-lon`}
								type="number"
								step="any"
								value={position.lon}
								onChange={(e) => setTypedPosition({ ...position, lon: e.target.value })}
								aria-invalid={!!positionError}
								className="bg-white"
							/>
						</div>
						<div>
							<Label htmlFor={`${positionFieldId}-lat`} className="text-xs text-muted-foreground mb-1 block">
								{t('common.labels.latitude')}
							</Label>
							<Input
								id={`${positionFieldId}-lat`}
								type="number"
								step="any"
								value={position.lat}
								onChange={(e) => setTypedPosition({ ...position, lat: e.target.value })}
								aria-invalid={!!positionError}
								className="bg-white"
							/>
						</div>
					</div>
					{positionError && <p className="mt-1 text-[11px] text-destructive">{positionError}</p>}
					<p className="mt-1 text-[11px] text-muted-foreground">
						{isSelfGeoreferenced ? t('files.upload.positionHintGeoreferenced') : t('files.upload.positionHintDefault')}
					</p>
				</div>
			</div>

			<DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
				{isUploadBusy && (
					<p className="text-[11px] text-muted-foreground self-center sm:mr-auto">
						{t('files.upload.uploadInProgressHint')}
					</p>
				)}
				<DialogClose asChild className="h-10 px-4 py-2">
					<Button variant="outline">{t('common.actions.cancel')}</Button>
				</DialogClose>
				<Button
					type="submit"
					className="h-10 px-4 py-2"
					disabled={!file || !name.trim() || isUploadBusy || !!positionError || isPlacing}
				>
					<Upload className="h-4 w-4 mr-2" />
					{isPlacing ? t('files.upload.placing') : t('common.actions.upload')}
				</Button>
			</DialogFooter>
		</form>
	);
}
